import { ChangeEvent, useEffect, useRef, useState } from 'react';
import {
    Box,
    Divider,
    FormControl,
    MenuItem,
    Radio,
    RadioGroup,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Card, CardContent, CardHeader, FormControlLabel, FormLabel } from './CampaignWizardStyles';

import { CampaignWizardStepProps } from './CampaignWizard';
import { CDCurrencyField } from '../../../../components/CDCurrencyField';
import { CDTextField } from '../../../../components/CDTextField';
import { useCampaignAudience } from '../../../../hooks/useCampaignAudience';
import { useCampaignWizardContext } from '../../../../hooks/useCampaignWizard';
import CampaignDuplicateLoader from '../CampaignDuplicateLoader';
import CDButton from '../../../../components/CDButton';
import Column from '../../../../components/Column';
import Row from '../../../../components/Row';
import { useCampaignFlights } from '../../flight/useCampaignFlights';
import FlightTableRow from '../../flight/FlightTableRow';
import { CampaignFlight } from '../../../../types/Campaign';
import { useUserContext } from '../../../../hooks/Contexts/UserContext';

type CampaignSetupDeliveryCardProps = {} & CampaignWizardStepProps;

export default function CampaignSetupDeliveryCard(props: CampaignSetupDeliveryCardProps) {
    const { campaign, onChange } = props;
    const { hasError, isDraftCampaign, isNewCampaign, setFlightValidation } = useCampaignWizardContext();
    const { userContext } = useUserContext();
    const showImpressionGoal = userContext.isAdmin();

    const [campaignBudget, setCampaignBudget] = useState<string>(campaign.budget ? String(campaign.budget) : '');
    const [isChangingCampaignBudget, setIsChangingCampaignBudget] = useState<boolean>(false);
    const [debouncedCampaignBudget, setDebouncedCampaignBudget] = useState<string>(
        campaign.budget ? String(campaign.budget) : ''
    );
    const debouncedBudgetTimeout = useRef<any | null>(null);
    // Remembers per-flight impression goals while in budget mode so switching
    // back to impression mode restores them (keyed by flight id or tempId).
    const rememberedImpressionGoals = useRef<Record<string, number | null>>({});

    useEffect(() => {
        const loaderTimeout = setTimeout(() => setIsChangingCampaignBudget(true), 500);
        debouncedBudgetTimeout.current = setTimeout(() => {
            setDebouncedCampaignBudget(campaignBudget);
            setIsChangingCampaignBudget(false);
        }, 2000);
        return () => {
            clearTimeout(loaderTimeout);
            clearTimeout(debouncedBudgetTimeout.current);
        };
    }, [campaignBudget]);

    useEffect(() => {
        if (debouncedCampaignBudget) {
            onChange({ budget: parseFloat(debouncedCampaignBudget) });
        }
    }, [debouncedCampaignBudget, onChange]);

    const $campaignAudience = useCampaignAudience({
        campaign,
        onChange: (budget: number, impressionGoal: number | null, impressionBased: boolean): void => {
            if (isNewCampaign || isDraftCampaign) {
                onChange({ budget, impressionGoal, impressionBased });
            } else {
                onChange({ impressionGoal, impressionBased });
            }
        },
    });
    const { impressionBased, setImpressionBased, calculateBudgetByImpression, calculateImpressionsByBudget } =
        $campaignAudience;

    const {
        flights,
        editingFlightId,
        isFlightLive,
        isFlightUpcoming,
        isFlightValid,
        getFlightMinStartDate,
        getFlightMaxEndDate,
        handleAddFlight,
        handleFlightChange,
        handleFlightChangeMultiple,
        handleSaveFlight,
        handleEditFlight,
        handleDeleteFlight,
    } = useCampaignFlights({
        campaign,
        onChange,
        setFlightValidation: setFlightValidation as ((valid: boolean) => void) | undefined,
    });

    const handleFlightImpressionGoalChange = (flightKey: string | number, impressionGoal: number | null) => {
        if (impressionGoal != null && impressionGoal > 0) {
            const estimatedBudget = calculateBudgetByImpression(impressionGoal);
            // Set both fields atomically so the budget updates in the same render
            handleFlightChangeMultiple(flightKey, {
                impressionGoal,
                budgetAmount: estimatedBudget,
            } as Partial<CampaignFlight>);
        } else {
            handleFlightChange(flightKey, 'impressionGoal' as keyof CampaignFlight, impressionGoal);
        }
    };

    // Auto-calculate impression goal from budget via CPM (reverse).
    // Only recalculates if the flight already has an impression goal —
    // budget-only flights are left alone.
    const handleFlightBudgetChange = (flightKey: string | number, budgetAmount: number) => {
        const flight = flights.find((f) => {
            const key = f.id !== undefined ? f.id : f.tempId;
            return key === flightKey;
        });
        const hasImpressionGoal = flight?.impressionGoal != null && flight.impressionGoal > 0;

        if (hasImpressionGoal) {
            const estimatedImpressions = calculateImpressionsByBudget(budgetAmount);
            handleFlightChangeMultiple(flightKey, {
                budgetAmount,
                impressionGoal: estimatedImpressions,
            } as Partial<CampaignFlight>);
        } else {
            handleFlightChange(flightKey, 'budgetAmount', budgetAmount);
        }
    };

    const handleModeChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newImpressionMode = event.target.value === 'IMPRESSION';
        setImpressionBased(newImpressionMode);
        onChange({ impressionBased: newImpressionMode });

        if (!newImpressionMode) {
            // Budget mode: remember each flight's goal, then clear it so the goal
            // is neither shown nor persisted. Also clear the campaign-level goal,
            // which otherwise forces the mode back to impression (useCampaignAudience).
            flights.forEach((flight) => {
                const key = flight.id !== undefined ? flight.id : flight.tempId!;
                rememberedImpressionGoals.current[String(key)] = flight.impressionGoal ?? null;
                handleFlightChange(key, 'impressionGoal' as keyof CampaignFlight, null);
            });
            onChange({ impressionGoal: null });
        } else {
            // Impression mode: restore any goals remembered from before the switch.
            flights.forEach((flight) => {
                const key = flight.id !== undefined ? flight.id : flight.tempId!;
                const remembered = rememberedImpressionGoals.current[String(key)];
                if (remembered != null && remembered > 0) {
                    handleFlightChange(key, 'impressionGoal' as keyof CampaignFlight, remembered);
                }
            });
        }
    };

    const isExistingCampaign = !isNewCampaign && !isDraftCampaign;

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.name) {
            onChange({ [event.target.name]: event.target.value });
        }
    };

    const now = new Date();
    const activeFlights = flights.filter((f) => {
        if (!f.endDateTime) return true;
        return new Date(f.endDateTime) >= now;
    });
    const completedCount = [...flights]
        .sort((a, b) => new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime())
        .filter((f) => f.endDateTime && new Date(f.endDateTime) < now).length;

    return (
        <Card id="campaign-wizard-setup-delivery" variant="outlined">
            <CardHeader
                title={
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body1" color="text.primary">
                            Schedule & Budget
                        </Typography>
                    </Stack>
                }
            />
            <CardContent>
                <Column>
                    <CampaignDuplicateLoader isLoading={isChangingCampaignBudget} />

                    {/* ── Flights ──────────────────────────────────────────── */}
                    <FormControl>
                        <FormLabel required sx={{ mb: 2 }}>
                            Add or Edit Flight Details
                        </FormLabel>

                        {editingFlightId && (
                            <Typography color="warning.main" variant="caption" sx={{ mb: 1, display: 'block' }}>
                                Please save or delete the flight in edit mode before proceeding
                            </Typography>
                        )}

                        <Box
                            sx={{
                                border: '1px solid',
                                borderColor: editingFlightId ? 'warning.main' : 'divider',
                                borderRadius: 1,
                                overflow: 'hidden',
                            }}
                        >
                            <Box
                                sx={{
                                    p: 2,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <Typography variant="body1" fontWeight={500}>
                                    Budget and Flights
                                </Typography>
                                <CDButton
                                    variant="contained"
                                    onClick={handleAddFlight}
                                    size="small"
                                    disabled={isNewCampaign || isDraftCampaign}
                                >
                                    Add Flight
                                </CDButton>
                            </Box>

                            {showImpressionGoal && (
                                <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                    <RadioGroup
                                        row
                                        value={impressionBased ? 'IMPRESSION' : 'BUDGET'}
                                        onChange={handleModeChange}
                                    >
                                        <FormControlLabel value="BUDGET" control={<Radio />} label="Set Budget" />
                                        <FormControlLabel
                                            value="IMPRESSION"
                                            control={<Radio />}
                                            label="Set Impression Goal"
                                        />
                                    </RadioGroup>
                                </Box>
                            )}

                            <Box sx={{ overflowX: 'auto' }}>
                                <Table size="small" sx={{ tableLayout: 'auto' }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>Flight No.</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Budget</TableCell>
                                            <TableCell>Spent</TableCell>
                                            <TableCell>Remaining</TableCell>
                                            {showImpressionGoal && (
                                                <TableCell sx={{ whiteSpace: 'nowrap' }}>Impression Goal</TableCell>
                                            )}
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>Start Date</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>End Date</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    Actions
                                                    <Tooltip title="Save or delete flight">
                                                        <HelpOutlineIcon
                                                            sx={{ fontSize: 16, color: 'text.secondary' }}
                                                        />
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {activeFlights.map((flight, index) => (
                                            <FlightTableRow
                                                key={flight.id ?? flight.tempId}
                                                flight={flight}
                                                displayIndex={activeFlights.length + completedCount - index}
                                                isEditing={
                                                    editingFlightId ===
                                                    (flight.id !== undefined ? flight.id : flight.tempId!).toString()
                                                }
                                                isLive={isFlightLive(flight)}
                                                isUpcoming={isFlightUpcoming(flight)}
                                                isExistingCampaign={isExistingCampaign}
                                                isAutoAdded={!!flight.autoAdded}
                                                showImpressionGoal={showImpressionGoal}
                                                impressionMode={impressionBased}
                                                onImpressionGoalChange={
                                                    showImpressionGoal ? handleFlightImpressionGoalChange : undefined
                                                }
                                                onBudgetChange={
                                                    showImpressionGoal ? handleFlightBudgetChange : undefined
                                                }
                                                getFlightMinStartDate={getFlightMinStartDate}
                                                getFlightMaxEndDate={getFlightMaxEndDate}
                                                isFlightValid={isFlightValid}
                                                onFlightChange={handleFlightChange}
                                                onSave={(key) => handleSaveFlight(key, setCampaignBudget)}
                                                onEdit={handleEditFlight}
                                                onDelete={handleDeleteFlight}
                                            />
                                        ))}

                                        {flights.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={9} align="center">
                                                    <Typography color="text.secondary" sx={{ py: 2 }}>
                                                        No flights added. Click "Add Flight" to create one.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Box>
                    </FormControl>

                    <Divider />

                    {/* ── Budget Cap ───────────────────────────────────────── */}
                    <FormControl>
                        <FormLabel error={hasError('budgetCapType')} required sx={{ mb: 2 }}>
                            Select a Budget Cap
                        </FormLabel>

                        <RadioGroup
                            name="budgetCapType"
                            defaultValue="UNCAPPED"
                            value={campaign.budgetCapType}
                            onChange={handleChange}
                        >
                            <FormControlLabel value="UNCAPPED" control={<Radio />} label="Uncapped" />

                            <Row gap={0}>
                                <FormControlLabel
                                    value="CUSTOM"
                                    control={<Radio />}
                                    label=""
                                    labelPlacement="end"
                                    sx={{ '&': { marginRight: 0 } }}
                                />
                                <Row>
                                    <CDTextField
                                        select
                                        variant="outlined"
                                        name="budgetCapRecurrenceTimePeriod"
                                        required={campaign.budgetCapType === 'CUSTOM'}
                                        disabled={campaign.budgetCapType !== 'CUSTOM'}
                                        error={hasError('budgetCapRecurrenceTimePeriod')}
                                        value={campaign.budgetCapRecurrenceTimePeriod ?? 'DAILY'}
                                        onChange={handleChange}
                                        slotProps={{ inputLabel: { shrink: true } }}
                                        sx={{ '& .MuiOutlinedInput-root': { paddingLeft: 0 }, width: 124 }}
                                    >
                                        <MenuItem value="DAILY">Daily</MenuItem>
                                        <MenuItem value="MONTHLY">Monthly</MenuItem>
                                    </CDTextField>

                                    {campaign.budgetCapType === 'CUSTOM' && (
                                        <CDCurrencyField
                                            variant="outlined"
                                            label="Amount"
                                            name="budgetCapAmount"
                                            required={campaign.budgetCapType === 'CUSTOM'}
                                            disabled={campaign.budgetCapType !== 'CUSTOM'}
                                            error={hasError('budgetCapAmount')}
                                            value={campaign.budgetCapAmount ?? ''}
                                            onChange={handleChange}
                                            sx={{ width: 104 }}
                                        />
                                    )}
                                </Row>
                            </Row>
                        </RadioGroup>
                    </FormControl>
                </Column>
            </CardContent>
        </Card>
    );
}
